import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { RESERVED_COLUMN_IDS } from './utils';

interface ContentWidths {
  /** Widest header or cell with its content on one line. */
  content: Record<string, number>;
  /** Widest unbreakable piece of a header or cell. */
  floor: Record<string, number>;
}

const unseen = (key?: unknown) => ({ key, content: {}, floor: {} });

const TABLE_CONTAINER_ATTRIBUTE = 'data-table-container';

const CELL = '[data-column-id]';

const horizontalChrome = (el: HTMLElement) => {
  const style = getComputedStyle(el);
  return (
    parseFloat(style.paddingLeft) +
    parseFloat(style.paddingRight) +
    parseFloat(style.borderLeftWidth) +
    parseFloat(style.borderRightWidth)
  );
};

// A nested DataTable's cells belong to that table.
const ownCells = (container: HTMLElement, cells: Iterable<HTMLElement>) =>
  Array.from(cells).filter(
    (cell) => cell.closest(`[${TABLE_CONTAINER_ATTRIBUTE}]`) === container
  );

// Cells a batch of row mutations added or changed.
const mutatedCells = (container: HTMLElement, records: MutationRecord[]) => {
  const cells = new Set<HTMLElement>();
  records.forEach(({ target, addedNodes }) => {
    const owner =
      target instanceof Element ? target.closest<HTMLElement>(CELL) : null;
    if (owner) cells.add(owner);
    addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches(CELL)) cells.add(node);
      node
        .querySelectorAll<HTMLElement>(CELL)
        .forEach((cell) => cells.add(cell));
    });
  });
  return ownCells(container, cells);
};

// Measures each of the table's own `[data-column-id]` cells' `[data-column-content]` box (or just
// `only`) at `max-content` and at `min-content`, one synchronous read each, then restores the boxes.
// Cell padding and borders, and a header's `[data-column-actions]` box (painted over the label's
// right edge on hover), are read once per column and cell kind and included in the widths.
export function measureColumnContent(
  container: HTMLElement,
  only?: HTMLElement[]
): ContentWidths {
  const cells =
    only ?? ownCells(container, container.querySelectorAll<HTMLElement>(CELL));
  const boxes = cells.map((cell) =>
    cell.querySelector<HTMLElement>('[data-column-content]')
  );
  const chromeByKind = new Map<string, number>();
  // Read during the first pass so the actions box adds no layout of its own.
  const chrome = (cell: HTMLElement) => {
    const kind = `${cell.tagName}:${cell.dataset.columnId}`;
    if (!chromeByKind.has(kind)) {
      const actions =
        cell.tagName === 'TH'
          ? cell.querySelector<HTMLElement>('[data-column-actions]')
          : null;
      chromeByKind.set(
        kind,
        horizontalChrome(cell) + (actions?.getBoundingClientRect().width ?? 0)
      );
    }
    return chromeByKind.get(kind) ?? 0;
  };
  const saved = boxes.map((box) => box?.style.cssText ?? '');
  const widest = (sizing: 'max-content' | 'min-content') => {
    boxes.forEach((box) => {
      if (!box) return;
      box.style.flex = 'none';
      box.style.width = sizing;
    });
    const widths: Record<string, number> = {};
    cells.forEach((cell, index) => {
      const id = cell.dataset.columnId;
      const width = boxes[index]?.getBoundingClientRect().width;
      if (!id || !width) return;
      widths[id] = Math.max(widths[id] ?? 0, Math.ceil(width + chrome(cell)));
    });
    return widths;
  };
  const content = widest('max-content');
  const floor = widest('min-content');
  boxes.forEach((box, index) => {
    if (box) box.style.cssText = saved[index];
  });
  return { content, floor };
}

// Column widths from measured content. A column takes its one-line width. A wrap column starts
// at its widest unbreakable piece and grows into the space left, up to its one-line width;
// space still left is shared by every sized column. When the table is too
// wide even so, each wrap column takes an equal share of the container instead, bounded by
// those same two widths. Columns in `keep` (or never measured) keep their `prev` width.
export function allocateContentWidths({
  prev,
  columnIds,
  measured: { content, floor },
  wrapIds,
  keep,
  containerWidth,
}: {
  prev: Record<string, number>;
  columnIds: string[];
  measured: ContentWidths;
  wrapIds: Set<string>;
  keep: Set<string>;
  containerWidth: number;
}): Record<string, number> {
  const sized = columnIds.filter((id) => !keep.has(id) && content[id]);
  const wrapping = sized.filter((id) => wrapIds.has(id));
  const next = { ...prev };
  sized.forEach((id) => {
    next[id] = wrapIds.has(id) ? (floor[id] ?? content[id]) : content[id];
  });
  const total = () => columnIds.reduce((sum, id) => sum + (next[id] ?? 0), 0);
  // Too wide even at their narrowest: each wrap column gets an equal share of what is visible.
  if (wrapping.length && total() > containerWidth) {
    const share = containerWidth / wrapping.length;
    wrapping.forEach((id) => {
      next[id] = Math.min(content[id], Math.max(next[id], share));
    });
  }
  let free = containerWidth - total();
  wrapping.forEach((id, index) => {
    const extra = Math.max(free, 0) / (wrapping.length - index);
    const grown = Math.min(content[id], next[id] + extra);
    free -= grown - next[id];
    next[id] = grown;
  });
  if (free > 0 && sized.length)
    sized.forEach((id) => {
      next[id] += free / sized.length;
    });
  return next;
}

// Sizes columns to their content whenever rows render (a virtualized scroll, a refetch), and
// re-allocates what was measured when the container resizes. Widths
// only grow while `contentKey` stays the same, so scrolling never shrinks a column; new rows start
// over. Reserved columns, `fixedColumnIds` and those in `resizedIds` keep their width. Returns a reset that forgets
// what was measured and measures again.
export function useContentWidths({
  containerRef,
  columnIds,
  wrapColumnIds,
  fixedColumnIds,
  contentKey,
  enabled,
  resizedIds,
  setWidths,
}: {
  containerRef: RefObject<HTMLElement>;
  columnIds: string[];
  wrapColumnIds?: string[];
  fixedColumnIds?: string[];
  contentKey: unknown;
  enabled: boolean;
  resizedIds: MutableRefObject<Set<string>>;
  setWidths: Dispatch<SetStateAction<Record<string, number>>>;
}): () => void {
  const [tick, setTick] = useState(0);
  const seenRef = useRef<{ key: unknown } & ContentWidths>(unseen());
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return undefined;
    if (seenRef.current.key !== contentKey)
      seenRef.current = unseen(contentKey);
    const wrapIds = new Set(wrapColumnIds);
    // Null measures every cell; otherwise only the cells rows added (widths only grow), and
    // none when only the container resized.
    let pending: Set<HTMLElement> | null = null;
    const apply = () => {
      const seen = seenRef.current;
      if (!pending || pending.size) {
        const measured = measureColumnContent(
          container,
          pending ? Array.from(pending) : undefined
        );
        (['content', 'floor'] as const).forEach((kind) =>
          Object.entries(measured[kind]).forEach(([id, width]) => {
            seen[kind][id] = Math.max(seen[kind][id] ?? 0, width);
          })
        );
      }
      pending = new Set();
      const keep = new Set<string>([
        RESERVED_COLUMN_IDS.EXPANDER,
        RESERVED_COLUMN_IDS.SELECT,
        ...(fixedColumnIds ?? []),
        ...resizedIds.current,
      ]);
      setWidths((prev) => {
        const next = allocateContentWidths({
          prev,
          columnIds,
          measured: seen,
          wrapIds,
          keep,
          containerWidth: container.clientWidth,
        });
        return columnIds.some((id) => next[id] !== prev[id]) ? next : prev;
      });
    };
    let frame = requestAnimationFrame(apply);
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    // Rows rendering measure the new cells; the container resizing (a filter panel opening)
    // re-allocates the widths already measured.
    const rows = new MutationObserver((records) => {
      const cells = mutatedCells(container, records);
      if (!cells.length) return;
      cells.forEach((cell) => pending?.add(cell));
      schedule();
    });
    rows.observe(container, { childList: true, subtree: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      rows.disconnect();
      resize.disconnect();
    };
  }, [
    containerRef,
    columnIds,
    wrapColumnIds,
    fixedColumnIds,
    contentKey,
    enabled,
    resizedIds,
    setWidths,
    tick,
  ]);
  return useCallback(() => {
    seenRef.current = unseen();
    setTick((value) => value + 1);
  }, []);
}
